import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Dashboard from './pages/Dashboard';
import CreateTask from './pages/CreateTask';
import Management from './pages/Management';
import Analytics from './pages/Analytics';
import Features from './pages/Features';
import Ideas from './pages/Ideas';
import QuickTasks from './pages/QuickTasks';
import Releases from './pages/Releases';
import PersonLane from './pages/PersonLane';
import Initiatives from './pages/Initiatives';
import Dependencies from './pages/Dependencies';
import Chat from './pages/Chat';
import Uploads from './pages/Uploads';
import Reports from './pages/Reports';
import ApprovalQueue from './pages/ApprovalQueue';
import Campaigns from './pages/Campaigns';
import Responses from './pages/Responses';
import MyAIRepresentatives from './pages/MyAIRepresentatives';
import TeamAIRepresentatives from './pages/TeamAIRepresentatives';
import Login from './components/Login';
import Access from './pages/Access';
import Navbar from './components/Navbar';
import ChatDrawer from './components/ChatDrawer';
import FramingBanner from './components/FramingBanner';
import TaskModal from './components/TaskModal';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { ChatProvider } from './context/ChatContext';
import { IdentityProvider } from './context/IdentityContext';
import { CockpitUser, clearToken, consumeTokenFromUrl, fetchMe, getToken, logout as endSession } from './services/auth';
import { SESSION_EXPIRED_EVENT } from './services/api';
import { TaskModalProvider } from './context/TaskModalContext';

// Layout wrapper that includes Navbar
const Layout: React.FC<{ children: React.ReactNode; onLogout: () => void }> = ({ children, onLogout }) => {
  const { theme } = useTheme();

  return (
    <div style={{ minHeight: '100vh', backgroundColor: theme.background, paddingTop: '56px', overflowX: 'hidden', maxWidth: '100vw' }}>
      <Navbar onLogout={onLogout} />
      <FramingBanner />
      <main style={{ overflowX: 'hidden' }}>{children}</main>
      <ChatDrawer />

      {/* Global mobile styles */}
      <style>{`
        * {
          box-sizing: border-box;
        }

        html, body {
          margin: 0;
          padding: 0;
          overflow-x: hidden;
          max-width: 100vw;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        /* Mobile-friendly inputs */
        input, textarea, select, button {
          font-size: 16px; /* Prevents iOS zoom on focus */
        }

        /* Responsive container */
        .container {
          width: 100%;
          max-width: 1400px;
          margin: 0 auto;
          padding: 16px;
        }

        @media (max-width: 768px) {
          .container {
            padding: 12px;
          }

          /* Stack grid layouts on mobile */
          .responsive-grid {
            grid-template-columns: 1fr !important;
          }

          /* Full width cards on mobile */
          .card {
            margin: 0 0 12px 0 !important;
          }

          /* Smaller text on mobile */
          h1 {
            font-size: 1.5rem !important;
          }

          h2 {
            font-size: 1.25rem !important;
          }

          /* Better touch targets */
          button, a {
            min-height: 44px;
          }
        }

        /* Smooth scrolling */
        html {
          scroll-behavior: smooth;
        }

        /* Better focus states */
        *:focus-visible {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }
      `}</style>
    </div>
  );
};

function App() {
  // Who is signed in, resolved from the server on boot (services/auth.ts). Null means
  // "show the login screen"; `checking` keeps us from flashing the login screen at
  // someone who has a perfectly good session.
  const [user, setUser] = useState<CockpitUser | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // A one-time sign-in link (scripts/manage_access.py login-link) arrives as ?token=.
    consumeTokenFromUrl();

    let cancelled = false;
    const resolveSession = async () => {
      if (!getToken()) {
        if (!cancelled) setChecking(false);
        return;
      }
      try {
        const me = await fetchMe();
        if (cancelled) return;
        if (!me.auth_enabled) {
          // Local dev with the gate switched off: don't stand in anyone's way.
          setUser(me.user);
        } else if (me.user && !me.needs_profile) {
          setUser(me.user);
        } else {
          // Signed in but never said who they are: back to the login flow, which
          // asks for the name and nothing else.
          clearToken();
        }
      } catch {
        if (!cancelled) clearToken();
      } finally {
        if (!cancelled) setChecking(false);
      }
    };
    resolveSession();

    // Any API call that comes back 401 (revoked, expired, deactivated) drops us here.
    const onExpired = () => setUser(null);
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => {
      cancelled = true;
      window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
    };
  }, []);

  const handleSignedIn = (signedIn: CockpitUser) => setUser(signedIn);

  const handleLogout = async () => {
    await endSession();
    setUser(null);
  };

  if (checking) {
    return (
      <ThemeProvider>
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>
          Loading...
        </div>
      </ThemeProvider>
    );
  }

  if (!user) {
    return (
      <ThemeProvider>
        <Login onSignedIn={handleSignedIn} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <IdentityProvider sessionUser={user ? { email: user.email, name: user.name, rep_slug: user.rep_slug } : null}>
        <ChatProvider>
          <TaskModalProvider>
          <Router>
            <Layout onLogout={handleLogout}>
              <Routes>
                <Route path="/" element={<Features />} />
              <Route path="/tasks" element={<Dashboard />} />
              <Route path="/create" element={<CreateTask />} />
              <Route path="/management" element={<Management />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/ideas" element={<Ideas />} />
              <Route path="/quicktasks" element={<QuickTasks />} />
              <Route path="/releases" element={<Releases />} />
              <Route path="/people" element={<PersonLane />} />
              <Route path="/ai-representatives" element={<MyAIRepresentatives />} />
              <Route path="/team-ai-representatives" element={<TeamAIRepresentatives />} />
              <Route path="/chat" element={<Chat />} />
              <Route path="/initiatives" element={<Initiatives />} />
              <Route path="/dependencies" element={<Dependencies />} />
              <Route path="/uploads" element={<Uploads />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/approval-queue" element={<ApprovalQueue />} />
              <Route path="/campaigns" element={<Campaigns />} />
              <Route path="/responses" element={<Responses />} />
              <Route path="/access" element={<Access currentUser={user} />} />
              <Route path="/integrations" element={<Navigate to="/dependencies" replace />} />
              <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </Layout>
            {/* The single, shared task modal — overlays any page when a task is opened
                via useTaskModal().openTask(id). Mounted inside the Router so its
                "Open full task page ↗" link works, and inside TaskModalProvider so
                every page (incl. the chat drawer) can open it. */}
            <TaskModal />
          </Router>
          </TaskModalProvider>
        </ChatProvider>
      </IdentityProvider>
    </ThemeProvider>
  );
}

export default App;
